export function isMathJaxElement(element: Element): boolean {
  if (element.tagName.toLowerCase().startsWith('mjx-')) {
    return true;
  }
  return /(^|\s)mjx-/.test(element.getAttribute('class') ?? '');
}

/**
 * MathJax's invisible duplicates: v2's assistive MathML and emptied preview, and the
 * staged-v4 assistive element. The visible glyphs are aria-hidden.
 */
export function isHiddenMathCopy(element: Element): boolean {
  return (
    element.tagName === 'MJX-ASSISTIVE-MML' ||
    element.classList.contains('MJX_Assistive_MathML') ||
    element.classList.contains('MathJax_Preview')
  );
}

/**
 * True when the container holds math MathJax will still rewrite — a
 * `.tex2jax_process` element without MathJax's `math/tex` output yet. MathJax
 * typesets ~250ms after mount and shifts text offsets, so callers defer the
 * first paint until it settles to avoid a flash against the stale DOM.
 */
export function hasUntypesetMath(container: Element): boolean {
  if (!window.MathJax?.Hub) {
    return false;
  }
  return Array.from(container.querySelectorAll('.tex2jax_process')).some(
    (element) => !element.querySelector('script[type^="math/tex"]')
  );
}

/**
 * The outermost formula element holding `node` below `container`. MathJax owns its
 * glyph markup and assistive MathML, so nothing may be inserted inside either.
 */
export const outermostMathOf = (node: Node, container: Element): Element | null => {
  let math: Element | null = null;
  for (let element = node.parentElement; element && element !== container;) {
    if (
      isMathJaxElement(element) ||
      element.tagName.toLowerCase() === 'math' ||
      /(^|\s)MathJax/.test(element.getAttribute('class') ?? '')
    ) {
      math = element;
    }
    element = element.parentElement;
  }
  return math;
};

/**
 * The last node a formula owns. MathJax v2 finds a formula's output as the previous
 * sibling of its source script, so nothing may sit between the two.
 */
export const lastNodeOfMath = (math: Element): Node => {
  const next = math.nextSibling;
  return next instanceof HTMLScriptElement && next.type.startsWith('math/') ? next : math;
};
